<!--
给指定元素设置弹出面板，
主要为首页展示二级菜单优化开发（系统已有的link-popper, link-popover不满足需求），
类似vue-popper，但属性配置相对少和简单
若有其他需求可进行更深度开发
@author 姚润发
@created 2020/08/14 11:15
-->
<template>
	<component :is="tagName" :class="{'is-active-popper': forceShow || showPopper}" v-link-clickoutside="clickOutSide">
		<transition :name="transition">
			<span v-show="forceShow || showPopper" class="link-popper-tip-content">
				<slot name="popper"></slot>
			</span>
		</transition>
		<slot></slot>
	</component>
</template>

<script>
  import PopperJS from 'popper.js'
  import deepmerge from 'deepmerge';

  /**
   * 绑定事件操作，兼容ie
   * @type {Function}
   */
  const on = (function () {
    if (document.addEventListener) {
      return function (element, event, handler) {
        if (element && event && handler) {
          element.addEventListener(event, handler, false);
        }
      };
    }
    return function (element, event, handler) {
      if (element && event && handler) {
        element.attachEvent(`on${event}`, handler);
      }
    };
  }());

  /**
   * 移除绑定事件操作
   * @type {Function}
   */
  const off = (function () {
    if (document.removeEventListener) {
      return function (element, event, handler) {
        if (element && event) {
          element.removeEventListener(event, handler, false);
        }
      };
    }
    return function (element, event, handler) {
      if (element && event) {
        element.detachEvent(`on${event}`, handler);
      }
    };
  }());

  export default {
    name: 'link-popper-tip',
    props: {
      // 组件的dom标签
      tagName: {
        type: String,
        default: 'span'
      },
      // 动画名称，内置只提供slide，其他效果需要自行处理
      transition: {
        type: String,
        default: 'slide'
      },
      // popper是否直接添加到body节点
      appendToBody: {
        type: Boolean,
        default: true
      },
      // popper实例化配置项
      options: {
        type: Object,
        default: () => {
          return {}
        }
      },
      // 触发方式
      trigger: {
        type: String,
        default: 'hover'
      },
      // 点击reference时是否强制显示popper，否则来回切换显示与隐藏
      forceShow: Boolean,
      // 延迟弹出，收起面板时间（ms）
      delayShowTime: {
        type: Number,
        default: 50
      },
    },
    data() {
      return {
        // 引用元素
        referenceElm: null,
        // 弹出元素
        popperElm: null,
        // Popper实例
        popperObj: null,
        // popper 配置选项, 参考标准popper v1.x文档 https://popper.js.org/docs/v1/
        popperOptions: {
          // 弹出元素位置相对referenceElm的位置
          placement: 'right-start',
          modifiers: {
            // 偏移配置
            offset:{
              // offset: '-160, 1600'
            },
			// 边界元素，默认边界为父滚动元素内部
            preventOverflow: {
              boundariesElement: 'window',
            },
			// GPU加速关闭
            computeStyle: {
              gpuAcceleration: false,
              x: 'bottom',
              y: 'left',
            },
          },
        },
        // 所有弹出触发方式
        TriggerType: {
          Hover: 'hover',
          Click: 'click',
          Focus: 'focus'
        },
        // 是否弹出面板
        showPopper: false,
        // 设置setTimeout返回的标记
        _timer: null
      }
    },
    watch: {
      showPopper(value) {
        if (value) {
          this.$emit('show', this);
          if (this.popperObj) {
            this.popperObj.enableEventListeners();
          }
          this.updatePopper();
        } else {
          if (this.popperObj) {
            this.popperObj.disableEventListeners();
          }
          this.$emit('hide', this);
        }
      },
    },
    created() {
      // 合并配置项
      this.popperOptions = deepmerge(this.popperOptions, this.options)
    },
    beforeDestroy() {
      this.destroy()
    },
    mounted() {
      // 获取dom元素
      this.referenceElm = this.$slots.default[0].elm
      this.popperElm = this.$slots.popper[0].elm

      // 设置监听事件
      switch (this.trigger) {
        case this.TriggerType.Hover:
          on(this.referenceElm, 'mouseover', this.onMouseOver);
          on(this.popperElm, 'mouseover', this.onMouseOver);
          on(this.referenceElm, 'mouseout', this.onMouseOut);
          on(this.popperElm, 'mouseout', this.onMouseOut);
          break;
        case this.TriggerType.Click:
          on(this.referenceElm, 'click', this.doClick);
          break;
        case this.TriggerType.Focus:
          on(this.referenceElm, 'focus', this.onMouseOver);
          on(this.popperElm, 'focus', this.onMouseOver);
          on(this.referenceElm, 'blur', this.onMouseOut);
          on(this.popperElm, 'blur', this.onMouseOut);
          break;
        default:
          break;
      }

      // this.updatePopper()
    },
    methods: {
      /**
       * 鼠标移到referenceElm上去
       */
      onMouseOver() {
        // console.log('mouse over')
        clearTimeout(this._timer)
        this._timer = setTimeout(() => {
          this.showPopper = true
        }, this.delayShowTime)
      },
      /**
       * 鼠标离开referenceElm
       */
      onMouseOut(e) {
        // console.log('mouse out', e.target)
        clearTimeout(this._timer)
        this._timer = setTimeout(() => {
          this.showPopper = false
        }, this.delayShowTime)
      },
      /**
       * 点击referenceElm打开popper
       * @param e
       */
      doClick(e) {
        if(this.stopPropagation) {
          e.stopPropagation();
        }
        if(this.preventDefault) {
          e.preventDefault();
        }
        if (!this.forceShow) {
          this.showPopper = !this.showPopper;
        }
      },
      /**
       * 点击组件外部元素，关闭popper
       */
      clickOutSide() {
        if(this.trigger === this.TriggerType.Click) {
          this.showPopper = false;
        }
      },

      /**
       * 创建popper实例
       */
      createPopper() {
        this.$nextTick(() => {
          if(this.appendToBody) {
            this.appendedToBody = true
            document.body.appendChild(this.popperElm.parentElement)
          }
          this.popperObj = new PopperJS(this.referenceElm, this.popperElm, this.popperOptions)
          // console.log(this.popperOptions)
        })
      },
      /**
       * 根据reference和参数重新计算定位popper元素
       */
      updatePopper() {
        // scheduleUpdate为异步更新，等待相关ui更新完后才更新
        this.popperObj ? this.popperObj.scheduleUpdate() : this.createPopper()
      },
      /**
       * 销毁popperElm
       */
      doDestroy() {
        if(this.popperObj) {
          this.popperObj.destroy()
          this.popperObj = null;
        }
        if(this.appendToBody && this.appendedToBody) {
          // 防止报错【The node to be removed is not a child of this node】
          this.appendedToBody = false
          document.body.removeChild(this.popperElm.parentElement)
        }
      },
      /**
       * 销毁popper元素及referenceElm上的监听事件
       */
      destroy() {
        // 删除referenceElm元素上的监听，popperElm会整个销毁，所以不需要单独对它移除事件
        off(this.referenceElm, 'mouseover', this.onMouseOver);
        off(this.referenceElm, 'mouseout', this.onMouseOut);
        off(this.referenceElm, 'click', this.doClick);
        off(this.referenceElm, 'focus', this.onMouseOver);
        off(this.referenceElm, 'blur', this.onMouseOut);

        this.doDestroy()
      }
    }
  }
</script>

<style lang="scss">

	.link-popper-tip-content{
		&>*{
			box-sizing: border-box;
			// box-shadow: 0 0 20px #ddd;
			transition: transform .3s;
			transform-origin: left top;
			// background-color: #fff;
			z-index: 999;
		}

		&.slide-enter>*,
		&.slide-leave-to>*{
			// 进入时由无变为正常，即0->1，所以进入那一瞬间还是scaleY(0)，然后移除（0->1）以触发过渡，即enter
			// 离开是由正常变无，即1->0，所以离开的过程中是scaleY(0)，以触发过渡
			// 所以vue过渡无的效果样式在enter和leave-to中做
			transform: scale(0);
		}
	}

</style>
